import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle, FileText, Shield, MessageCircle, ShoppingCart, Percent, MapPin,
  ArrowRight, Sparkles,
} from "lucide-react";
import ContactForm from "../components/ContactForm";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%20Wintrust%2C%20I%20need%20E-Commerce%20GST%20Registration.%20Please%20guide%20me.";
const WHATSAPP_BOTTOM_URL = "https://wa.me/918940988776?text=Hi%20Wintrust%2C%20I%20need%20E-Commerce%20GST%20Registration";

const PLATFORMS = [
  "Amazon", "Flipkart", "Meesho", "Myntra", "Shopsy", "JioMart",
  "Nykaa", "AJIO", "Snapdeal", "Shopify (D2C)", "WooCommerce", "Your Own Website",
];

const FAQS = [
  { q: "Is GST registration mandatory for e-commerce sellers?", a: "Yes. Under Section 24(ix) of the CGST Act, GST registration is mandatory for all e-commerce sellers regardless of turnover. Even if your annual sales are below ₹40 lakh, you must register to sell on any online marketplace." },
  { q: "How long does e-commerce GST registration take?", a: "E-commerce GST registration typically takes 3–7 working days if all documents are correctly submitted. Aadhaar-verified applications are processed faster." },
  { q: "Can I use my home address for GST registration?", a: "Yes. Home-based e-commerce sellers can use their residential address. You need an electricity bill in your name plus a self-declaration. For rented premises, a rental agreement and NOC from the property owner are required." },
  { q: "What is TCS in e-commerce GST?", a: "TCS (Tax Collected at Source) is 1% of the net taxable value of sales, collected by the marketplace (Amazon, Flipkart, etc.) and deposited with the government. As a seller, you can claim this TCS as credit when filing your GST returns." },
  { q: "Do I need separate GST registration in multiple states?", a: "Only if you store inventory in multiple states (e.g., through Amazon FBA or Flipkart Fulfilment warehouses). If you ship only from your home state, a single GSTIN is sufficient." },
  { q: "Can e-commerce sellers use the Composition Scheme?", a: "No. E-commerce sellers are excluded from the Composition Scheme under Section 10(2) of the CGST Act. You must file regular monthly returns." },
  { q: "What happens if I sell without GST registration?", a: "You face a penalty of 10% of tax due (minimum ₹10,000) plus 18% interest on unpaid tax. Additionally, no marketplace will activate your seller account without a valid GSTIN." },
];

const KEY_THINGS = [
  { title: "Composition Scheme Not Available", body: "E-commerce sellers are explicitly excluded from the Composition Scheme under Section 10(2) of the CGST Act. You must file regular monthly returns (GSTR-1 and GSTR-3B), regardless of turnover." },
  { title: "Multi-State Registration for FBA / Warehouses", body: "If you use Fulfilment by Amazon (FBA) or Flipkart Fulfilment (FBF) and your stock is stored in warehouses across multiple states, you need a separate GSTIN in each state where inventory is placed. Stock transfers between your own warehouses are treated as inter-state supply." },
  { title: "HSN Code Selection is Critical", body: "Every product you sell must have the correct HSN code on your invoice. Wrong HSN codes create downstream mismatches in every GSTR-1 you file and can trigger scrutiny from the GST department. Get it right at registration itself." },
  { title: "AI-Assisted Verification in 2026", body: "The GSTN portal now uses AI-based document verification. Low-quality scans, blurry uploads, or oversized files get flagged automatically. All documents must be clear, legible, and under 1MB." },
  { title: "Penalty for Non-Registration", body: "Operating without GST registration on a marketplace attracts a penalty of 10% of tax due (minimum ₹10,000) plus 18% interest on unpaid tax from the date registration was legally required." },
];

const PRICING = [
  {
    name: "Basic",
    tagline: "Single State Registration",
    price: "₹1,999",
    suffix: "onwards",
    features: [
      "GST Registration (1 state)",
      "Document preparation & verification",
      "Application filing + follow-up",
      "GSTIN delivery in 3–7 working days",
    ],
    highlight: false,
  },
  {
    name: "Standard",
    tagline: "Registration + Platform Setup",
    price: "₹2,499",
    suffix: "onwards",
    features: [
      "Everything in Basic",
      "HSN code mapping for your products",
      "GSTIN configuration on Amazon / Flipkart / Meesho",
      "First month GST filing guidance",
    ],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Multi-State",
    tagline: "FBA / Multi-Warehouse Sellers",
    price: "₹3,999",
    suffix: "onwards (per additional state)",
    features: [
      "Registration in multiple states",
      "FBA warehouse state identification",
      "Complete multi-GSTIN compliance setup",
      "Ongoing filing support available",
    ],
    highlight: false,
  },
];

const EcomGSTRegistrationPage = () => {
  useCanonical("/ecom-gst-registration");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "E-Commerce GST Registration in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Mandatory GST registration for e-commerce sellers on Amazon, Flipkart, Meesho and all marketplaces. Expert GSTIN setup in 3–7 working days.",
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>E-Commerce GST Registration in Coimbatore | Amazon, Flipkart, Meesho Sellers | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Mandatory GST registration for e-commerce sellers on Amazon, Flipkart, Meesho & all marketplaces. Expert GSTIN setup in 3–7 working days. Hassle-free. WINTRUST SOLUTIONS Coimbatore." />
        <meta property="og:title" content="E-Commerce GST Registration | Sell Legally on Amazon, Flipkart, Meesho" />
        <meta property="og:description" content="GST registration is mandatory for ALL e-commerce sellers — no turnover exemption. Get your GSTIN in 3–7 days with Wintrust Solutions." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4" data-testid="ecom-reg-breadcrumb">
              <Link to="/" className="hover:text-gold">Home</Link> /{" "}
              <span className="text-slate-300">Services</span> /{" "}
              <span className="text-white">E-Commerce GST Registration</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading" data-testid="ecom-reg-h1">
              E-Commerce GST Registration in Coimbatore | Quick, Hassle-Free & Compliant
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Selling on Amazon, Flipkart, Meesho, or Myntra? Under Section 24(ix) of the CGST Act, GST registration is mandatory for ALL e-commerce sellers — regardless of turnover. At WINTRUST SOLUTIONS, we handle your complete e-commerce GST registration so you can start selling legally without delays.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary" data-testid="ecom-reg-cta-primary">Get Expert GST Support</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp" data-testid="ecom-reg-cta-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Mandatory */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Why is GST Registration Mandatory for E-Commerce Sellers?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: <Shield size={24} />, title: "Zero Turnover Threshold", desc: "Unlike offline businesses (exempted up to ₹40 lakh), e-commerce sellers must register for GST from their very first sale. Section 24(ix) of the CGST Act removes the turnover exemption entirely for marketplace sellers." },
              { icon: <ShoppingCart size={24} />, title: "Marketplace Account Activation", desc: "Amazon, Flipkart, Meesho, Myntra, Nykaa, JioMart, and all major platforms require a valid GSTIN before activating your seller account. No GSTIN = No selling." },
              { icon: <Percent size={24} />, title: "TCS (Tax Collected at Source) Compliance", desc: "E-commerce operators collect 1% TCS (0.5% CGST + 0.5% SGST) on every sale. This system only works when every seller has an active GSTIN. Without registration, TCS credits cannot be tracked or claimed." },
              { icon: <MapPin size={24} />, title: "Inter-State Selling", desc: "Most e-commerce sales are inter-state (buyer in different state than seller). Even a single inter-state shipment triggers mandatory GST registration with no exemption. IGST applies on all such transactions." },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-slate-50 rounded-lg">
                <div className="text-gold flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Strip */}
      <section className="bg-slate-50 py-12 md:py-14">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-2 font-heading text-center">
            Platforms We Cover
          </h2>
          <p className="text-slate-600 text-center text-sm md:text-base mb-7">
            We handle GST registration for sellers on ALL online platforms — marketplaces and D2C websites.
          </p>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3" data-testid="ecom-reg-platforms">
            {PLATFORMS.map((p) => (
              <span
                key={p}
                className="inline-block bg-white text-navy text-sm font-medium px-4 py-2 rounded-full border border-slate-200 shadow-sm hover:border-gold hover:text-gold transition-colors"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Our E-Commerce GST Registration Process
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Free Consultation", desc: "We start with a free consultation to understand your e-commerce business structure, platforms you sell on, and determine your GST requirements. We advise on single-state vs. multi-state registration based on your warehouse / FBA locations." },
              { step: "02", title: "Document Collection", desc: "We assist you in gathering all necessary documents — PAN, Aadhaar, address proof, bank details, and business documents. We verify everything for accuracy before submission to avoid REG-03 queries." },
              { step: "03", title: "Application Filing", desc: "Our experts prepare and file your GST registration application (Form GST REG-01) with complete accuracy. We select the correct registration reason: 'Liable to register under Section 24' for e-commerce sellers. Aadhaar authentication is completed for faster processing." },
              { step: "04", title: "GSTIN Issued", desc: "We track the application status and ensure you receive your 15-digit GSTIN promptly — typically within 3–7 working days. We also help you configure your GSTIN on your seller dashboard (Amazon Seller Central, Flipkart Seller Hub, etc.)." },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-gold font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Documents Required for E-Commerce GST Registration
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "PAN Card of the Business Owner / Company",
              "Aadhaar Card of the Authorized Signatory",
              "Proof of Business Address (Electricity Bill, Rent Agreement + NOC)",
              "Passport Size Photograph",
              "Bank Account Statement / Cancelled Cheque",
              "Digital Signature Certificate (for Companies / LLPs)",
              "Certificate of Incorporation (for Companies)",
              "Partnership Deed (for Partnership Firms)",
            ].map((doc, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200">
                <CheckCircle className="text-gold flex-shrink-0" size={20} />
                <span className="text-navy">{doc}</span>
              </div>
            ))}
          </div>
          <p className="text-slate-600 text-sm mt-6 max-w-3xl">
            <strong className="text-navy">Home-based sellers:</strong> A residential electricity bill + self-declaration is accepted. For rented premises, include NOC from the property owner.
          </p>
        </div>
      </section>

      {/* Key Things to Know */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Key Things E-Commerce Sellers Must Know
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {KEY_THINGS.map((item, i) => (
              <div key={i} className="bg-slate-50 border-l-4 border-gold rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <span className="bg-gold text-navy font-bold rounded-full w-7 h-7 flex items-center justify-center text-sm flex-shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-navy mb-1.5">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-2 font-heading text-center">
            Transparent Pricing
          </h2>
          <p className="text-slate-600 text-center mb-10">Choose the plan that fits your e-commerce setup</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="ecom-reg-pricing">
            {PRICING.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl p-7 border-2 transition-all ${
                  plan.highlight
                    ? "border-gold shadow-xl shadow-gold/10 md:-translate-y-2"
                    : "border-slate-200 hover:border-gold/40 hover:shadow-md"
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <Sparkles size={12} /> {plan.badge}
                  </span>
                )}
                <h3 className="text-xl font-semibold text-navy font-heading">{plan.name}</h3>
                <p className="text-slate-500 text-sm mt-1 mb-4">{plan.tagline}</p>
                <p className="text-3xl font-bold text-navy font-heading">
                  {plan.price}
                  <span className="text-sm text-slate-500 font-normal ml-1">{plan.suffix}</span>
                </p>
                <ul className="mt-5 space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-700 text-sm">
                      <CheckCircle className="text-gold flex-shrink-0 mt-0.5" size={16} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-6 w-full block text-center font-semibold py-3 rounded-md transition-colors ${
                    plan.highlight
                      ? "bg-navy text-white hover:bg-navy-900"
                      : "border-2 border-navy text-navy hover:bg-navy hover:text-white"
                  }`}
                  data-testid={`ecom-reg-pricing-cta-${plan.name.toLowerCase()}`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white section-padding">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="ecom-reg-faq">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`q-${i}`}
                className="bg-slate-50 rounded-lg border border-slate-200 px-6"
                data-testid={`ecom-reg-faq-${i}`}
              >
                <AccordionTrigger className="text-left text-navy font-semibold py-4 hover:no-underline hover:text-gold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal Links */}
      <section className="bg-slate-50 py-10">
        <div className="container-custom">
          <h2 className="text-xl md:text-2xl font-semibold text-navy mb-5 font-heading">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-3">
            <Link to="/ecom-gst-filing" className="bg-white border border-slate-200 hover:border-gold hover:text-gold rounded-xl px-5 py-4 text-navy text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors">
              <span>E-Commerce GST Filing</span><ArrowRight size={16} />
            </Link>
            <Link to="/gst-registration" className="bg-white border border-slate-200 hover:border-gold hover:text-gold rounded-xl px-5 py-4 text-navy text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors">
              <span>GST Registration (General)</span><ArrowRight size={16} />
            </Link>
            <Link to="/gst-return-filing" className="bg-white border border-slate-200 hover:border-gold hover:text-gold rounded-xl px-5 py-4 text-navy text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors">
              <span>GST Return Filing</span><ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy py-14">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4 font-heading">
            Ready to Start Selling on E-Commerce Platforms?
          </h2>
          <p className="text-slate-300 mb-7 max-w-2xl mx-auto">
            Don't let compliance delays stop your e-commerce business. Contact WINTRUST SOLUTIONS today for a free consultation and let our experts handle your GST registration from start to finish.
          </p>
          <a
            href={WHATSAPP_BOTTOM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp inline-flex"
            data-testid="ecom-reg-bottom-cta"
          >
            <MessageCircle size={18} /> Get Expert GST Support
          </a>
        </div>
      </section>

      <ContactForm />
    </>
  );
};

export default EcomGSTRegistrationPage;
